"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import { useGlobalModalStack, MODAL_IDS } from "@wtc/ui";
import type { BaseEntity } from "../types";

/**
 * Selection, bulk-action and row-open state for the mobile list.
 *
 * Split out of `SimpleTableMobileView.tsx` (module-size lane L17). Behaviour is
 * pinned by `tests/components-ui/SimpleTableMobileView*.test.tsx`.
 */
export function useMobileViewState<T extends BaseEntity>({
  data,
  onBulkAction,
  onOpenDetailModal,
  onView,
}: {
  data: T[];
  onBulkAction?: (actionId: string, selectedItems: T[]) => void;
  onOpenDetailModal?: (item: T, ids: string[], index: number) => void;
  onView?: (item: T) => void;
}) {
  const { openModal, closeModal } = useGlobalModalStack();

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [actionsItem, setActionsItem] = useState<T | null>(null);
  const [isActionLoading, setIsActionLoading] = useState<string | null>(null);
  /** Set when a bulk action rejected — this used to be console-only. */
  const [bulkActionError, setBulkActionError] = useState<string | null>(null);
  /** The action id that failed, so Retry replays exactly it. */
  const lastBulkActionRef = useRef<string | null>(null);

  const handleOpenActions = useCallback(
    (item: T) => {
      setActionsItem(item);
      openModal(MODAL_IDS.navigation.mobileRowActions);
    },
    [openModal]
  );

  const selectedData = useMemo(
    () => data.filter((item) => selectedItems.includes(item.id)),
    [data, selectedItems]
  );

  const handleItemSelect = useCallback((itemId: string, selected: boolean) => {
    setSelectedItems((prev) => {
      if (selected) {
        return [...prev, itemId];
      }
      return prev.filter((id) => id !== itemId);
    });
  }, []);

  const handleBulkAction = useCallback(
    async (actionId: string) => {
      if (onBulkAction && selectedItems.length > 0) {
        lastBulkActionRef.current = actionId;
        setIsActionLoading(actionId);
        setBulkActionError(null);
        try {
          await onBulkAction(actionId, selectedData);
          setSelectedItems([]);
        } catch (err) {
          // Was console-only: the user watched the selection survive with no
          // message and no way to tell a failure from a no-op.
          console.error("Bulk action failed:", err);
          setBulkActionError("That action didn't go through. Your selection was kept.");
        } finally {
          setIsActionLoading(null);
        }
      }
    },
    [onBulkAction, selectedItems.length, selectedData]
  );

  /** Replay the bulk action that failed, on the surviving selection. */
  const retryBulkAction = useCallback(() => {
    const actionId = lastBulkActionRef.current;
    if (actionId) void handleBulkAction(actionId);
  }, [handleBulkAction]);

  const dismissBulkActionError = useCallback(() => setBulkActionError(null), []);

  const handleClearSelection = useCallback(() => {
    setSelectedItems([]);
    setBulkActionError(null);
  }, []);

  /**
   * Three-way routing, in priority order: the host's detail modal (which also
   * gets the id list + index so the modal can page through the results), then a
   * plain `onView`, then this component's own fallback modal.
   */
  const handleItemView = useCallback(
    (item: T) => {
      if (onOpenDetailModal && data) {
        const itemIndex = data.findIndex((d) => d.id === item.id);
        const itemIds = data.map((d) => d.id);
        onOpenDetailModal(item, itemIds, itemIndex);
      } else if (onView) {
        onView(item);
      } else {
        setSelectedItem(item);
        openModal(MODAL_IDS.navigation.mobileDetail);
      }
    },
    [onOpenDetailModal, onView, data, openModal]
  );

  const handleFallbackModalOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setSelectedItem(null);
    }
  }, []);

  const handleActionsModalOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setActionsItem(null);
    }
  }, []);

  const closeActionsSheet = useCallback(
    () => closeModal(MODAL_IDS.navigation.mobileRowActions),
    [closeModal]
  );

  return {
    selectedItems,
    selectedItem,
    actionsItem,
    isActionLoading,
    bulkActionError,
    retryBulkAction,
    dismissBulkActionError,
    handleItemSelect,
    handleBulkAction,
    handleClearSelection,
    handleItemView,
    handleOpenActions,
    handleFallbackModalOpenChange,
    handleActionsModalOpenChange,
    closeActionsSheet,
  };
}
